import { useEffect, useMemo } from 'react';
import { Outlet, ScrollRestoration, useParams } from 'react-router';
import { LocaleContext, getStringsFor, getDirFor } from '@/hooks/useLocale';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { isLocale, DEFAULT_LOCALE, type Locale } from '@/strings/types';
import TopNav from '@/components/Nav/TopNav';
import Footer from '@/components/Footer/Footer';
import NotFound from '@/routes/NotFound';

export default function Root() {
  const params = useParams();
  const rawLocale = params.locale;
  /* /:locale matches ANY first segment (dynamic outranks the top-level splat),
   * so /foobar used to render a coerced EN homepage at 200 — the site's
   * biggest soft-404. An unknown locale keeps the DEFAULT_LOCALE chrome but
   * renders NotFound instead of the outlet (Decision 3). */
  const valid = rawLocale != null && isLocale(rawLocale);
  const locale: Locale = valid ? rawLocale : DEFAULT_LOCALE;
  const dir = getDirFor(locale);
  const strings = getStringsFor(locale);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
  }, [locale, dir]);

  /* Sits beside the lang/dir effect: both correct the static index.html shell
     for the route actually being rendered. */
  useDocumentHead();

  const value = useMemo(() => ({ locale, dir, strings }), [locale, dir, strings]);

  return (
    <LocaleContext.Provider value={value}>
      {/* New page navigations (footer/nav links, sibling legal links) land at
          the top; back/forward restores the prior scroll. A location with a
          hash (the legal-page TOC anchors) scrolls to that section instead. */}
      <ScrollRestoration />
      <TopNav />
      {valid ? <Outlet /> : <NotFound />}
      <Footer />
    </LocaleContext.Provider>
  );
}
